import React, { ReactNode, forwardRef } from "react";
import clsx from "clsx";

// Base Radio Component
interface RadioProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  name?: string;
  value?: string;
  label?: string | ReactNode;
  description?: string;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  labelClassName?: string;
  id?: string;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      checked = false,
      onChange,
      name,
      value,
      label,
      description,
      disabled = false,
      error,
      helperText,
      size = "md",
      className,
      labelClassName,
      id,
      ...props
    },
    ref,
  ) => {
    const radioId = id || `radio-${Math.random().toString(36).substr(2, 9)}`;

    const sizeStyles = {
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-6 w-6",
    };

    const dotSizes = {
      sm: "h-2 w-2",
      md: "h-2.5 w-2.5",
      lg: "h-3 w-3",
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        onChange?.(e.target.checked);
      }
    };

    const radioClasses = clsx(
      "relative inline-flex items-center justify-center rounded-full border transition-all duration-200",
      sizeStyles[size],
      {
        // Unchecked state
        "bg-gray-900 border-gray-700": !checked && !disabled && !error,
        // Checked state
        "border-tracq-bright-blue bg-gray-900 ring-2 ring-tracq-bright-blue": checked && !disabled,
        // Disabled state
        "bg-gray-800 border-gray-700 cursor-not-allowed": disabled,
        // Error state
        "border-red-500": error && !disabled,
        // Hover state
        "hover:border-tracq-bright-blue": !disabled && !error && !checked,
      },
    );

    return (
      <div className={clsx("flex flex-col", className)}>
        <div className="flex items-start gap-3">
          {/* Hidden input */}
          <input
            ref={ref}
            type="radio"
            id={radioId}
            name={name}
            value={value}
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
            className="sr-only"
            {...props}
          />

          {/* Visual radio */}
          <label
            htmlFor={radioId}
            className={clsx(
              "flex items-center gap-3 cursor-pointer",
              disabled && "cursor-not-allowed",
              labelClassName,
            )}
          >
            <div className={radioClasses}>
              {checked && (
                <div className={clsx("bg-tracq-bright-blue rounded-full", dotSizes[size])} />
              )}
            </div>

            {/* Label and description */}
            {(label || description) && (
              <div className="flex flex-col">
                {label && (
                  <span
                    className={clsx(
                      "text-sm font-medium",
                      disabled ? "text-gray-500" : "text-gray-200",
                    )}
                  >
                    {label}
                  </span>
                )}
                {description && (
                  <span
                    className={clsx("text-xs mt-1", disabled ? "text-gray-500" : "text-gray-400")}
                  >
                    {description}
                  </span>
                )}
              </div>
            )}
          </label>
        </div>

        {/* Helper/Error text */}
        {(helperText || error) && (
          <div className={clsx("mt-2 text-sm", error ? "text-red-400" : "text-gray-400")}>
            {error || helperText}
          </div>
        )}
      </div>
    );
  },
);

Radio.displayName = "Radio";

// Radio Group Component
interface RadioGroupProps {
  options: Array<{
    value: string;
    label: string;
    description?: string;
    disabled?: boolean;
  }>;
  value?: string;
  onChange?: (value: string) => void;
  name?: string;
  label?: string;
  description?: string;
  error?: string;
  helperText?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  orientation?: "vertical" | "horizontal";
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
  options,
  value,
  onChange,
  name,
  label,
  description,
  error,
  helperText,
  size = "md",
  className,
  orientation = "vertical",
}) => {
  const groupName = name || `radio-group-${Math.random().toString(36).substr(2, 9)}`;

  const handleOptionChange = (optionValue: string) => {
    onChange?.(optionValue);
  };

  return (
    <div className={clsx("space-y-4", className)}>
      {/* Group label */}
      {(label || description) && (
        <div>
          {label && <h3 className="text-sm font-medium text-gray-200">{label}</h3>}
          {description && <p className="text-xs text-gray-400 mt-1">{description}</p>}
        </div>
      )}

      {/* Options */}
      <div
        className={clsx(
          "space-y-3",
          orientation === "horizontal" && "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
        )}
      >
        {options.map((option) => (
          <Radio
            key={option.value}
            name={groupName}
            value={option.value}
            checked={value === option.value}
            onChange={() => handleOptionChange(option.value)}
            label={option.label}
            {...(option.description !== undefined ? { description: option.description } : {})}
            {...(option.disabled !== undefined ? { disabled: option.disabled } : {})}
            size={size}
          />
        ))}
      </div>

      {/* Helper/Error text */}
      {(helperText || error) && (
        <div className={clsx("text-sm", error ? "text-red-400" : "text-gray-400")}>
          {error || helperText}
        </div>
      )}
    </div>
  );
};

// Card-Style Radio Component
interface CardRadioProps {
  options: Array<{
    value: string;
    label: string;
    description?: string;
    icon?: ReactNode;
    disabled?: boolean;
  }>;
  value?: string;
  onChange?: (value: string) => void;
  name?: string;
  label?: string;
  description?: string;
  error?: string;
  helperText?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  columns?: 1 | 2 | 3 | 4;
}

export const CardRadio: React.FC<CardRadioProps> = ({
  options,
  value,
  onChange,
  name,
  label,
  description,
  error,
  helperText,
  size = "md",
  className,
  columns = 2,
}) => {
  const groupName = name || `card-radio-${Math.random().toString(36).substr(2, 9)}`;

  const handleOptionChange = (optionValue: string) => {
    onChange?.(optionValue);
  };

  const gridCols = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  };

  return (
    <div className={clsx("space-y-4", className)}>
      {/* Group label */}
      {(label || description) && (
        <div>
          {label && <h3 className="text-sm font-medium text-gray-200">{label}</h3>}
          {description && <p className="text-xs text-gray-400 mt-1">{description}</p>}
        </div>
      )}

      {/* Card options */}
      <div className={clsx("grid gap-4", gridCols[columns])}>
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <label
              key={option.value}
              className={clsx(
                "relative flex flex-col items-center p-4 border rounded-lg cursor-pointer transition-all duration-200",
                "bg-gray-900 border-gray-700",
                isSelected
                  ? "border-tracq-bright-blue ring-2 ring-tracq-bright-blue bg-tracq-bright-blue/5"
                  : "hover:border-tracq-bright-blue hover:bg-gray-800",
                option.disabled && "opacity-50 cursor-not-allowed",
              )}
            >
              <input
                type="radio"
                name={groupName}
                value={option.value}
                checked={isSelected}
                onChange={() => handleOptionChange(option.value)}
                disabled={option.disabled}
                className="sr-only"
              />

              {/* Radio indicator */}
              <div className="absolute top-3 right-3">
                <div
                  className={clsx(
                    "relative inline-flex items-center justify-center rounded-full border transition-all duration-200",
                    size === "sm" ? "h-4 w-4" : size === "md" ? "h-5 w-5" : "h-6 w-6",
                    isSelected
                      ? "border-tracq-bright-blue bg-gray-900 ring-2 ring-tracq-bright-blue"
                      : "bg-gray-900 border-gray-700",
                  )}
                >
                  {isSelected && (
                    <div
                      className={clsx(
                        "bg-tracq-bright-blue rounded-full",
                        size === "sm" ? "h-2 w-2" : size === "md" ? "h-2.5 w-2.5" : "h-3 w-3",
                      )}
                    />
                  )}
                </div>
              </div>

              {/* Icon */}
              {option.icon && <div className="mb-3 text-gray-400">{option.icon}</div>}

              {/* Label */}
              <div className="text-center">
                <div
                  className={clsx(
                    "text-sm font-medium",
                    isSelected ? "text-tracq-bright-blue" : "text-gray-200",
                  )}
                >
                  {option.label}
                </div>
                {option.description && (
                  <div className="text-xs text-gray-400 mt-1">{option.description}</div>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {/* Helper/Error text */}
      {(helperText || error) && (
        <div className={clsx("text-sm", error ? "text-red-400" : "text-gray-400")}>
          {error || helperText}
        </div>
      )}
    </div>
  );
};

// Radio with Icon Component
interface RadioWithIconProps extends Omit<RadioProps, "label"> {
  icon: ReactNode;
  label: string;
  description?: string;
}

export const RadioWithIcon: React.FC<RadioWithIconProps> = ({
  icon,
  label,
  description,
  ...props
}) => {
  return (
    <Radio
      {...props}
      label={
        <div className="flex items-center gap-3">
          <div className="text-gray-400">{icon}</div>
          <div>
            <div className="text-sm font-medium text-gray-200">{label}</div>
            {description && <div className="text-xs text-gray-400 mt-1">{description}</div>}
          </div>
        </div>
      }
    />
  );
};
